/**
 * Axios Request Interceptor
 *
 * Handles:
 * - Authentication header injection
 * - Request ID generation for tracing
 * - Content-Type defaults
 * - Request/Response logging (dev only)
 */

import type { InternalAxiosRequestConfig } from "axios"

import type { TokenProvider } from "../types"

// Storage key for auth token (matches AuthContext)
const AUTH_TOKEN_KEY = "auth_token"

// Enable/disable API logging
const API_LOGGING_ENABLED = import.meta.env.DEV

/**
 * Default token provider using localStorage
 * Replace with custom implementation for different auth flows
 */
const defaultTokenProvider: TokenProvider = {
  getToken: () => localStorage.getItem(AUTH_TOKEN_KEY),
  clearTokens: () => {
    localStorage.removeItem(AUTH_TOKEN_KEY)
    localStorage.removeItem("auth_user")
  },
}

// Current token provider instance
let tokenProvider: TokenProvider = defaultTokenProvider

/**
 * Set a custom token provider
 */
export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider
}

/**
 * Get current token provider
 */
export function getTokenProvider(): TokenProvider {
  return tokenProvider
}

/**
 * Generate a unique request ID for tracing
 * Format: timestamp-random
 */
function generateRequestId(): string {
  const timestamp = Date.now().toString(36)
  const random = Math.random().toString(36).substring(2, 9)
  return `${timestamp}-${random}`
}

/**
 * Log API request (dev only)
 */
function logRequest(config: InternalAxiosRequestConfig): void {
  if (!API_LOGGING_ENABLED) return

  const method = config.method?.toUpperCase() ?? "GET"
  const url = `${config.baseURL ?? ""}${config.url ?? ""}`

  console.groupCollapsed(
    `%c→ ${method} %c${url}`,
    "color: #61affe; font-weight: bold",
    "color: #999"
  )
  console.log("%cHeaders:", "font-weight: bold", config.headers)
  if (config.params) {
    console.log("%cParams:", "font-weight: bold", config.params)
  }
  if (config.data) {
    console.log("%cBody:", "font-weight: bold", config.data)
  }
  console.groupEnd()
}

/**
 * Request interceptor - modifies outgoing requests
 */
export function requestInterceptor(
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig {
  // Skip auth header if explicitly disabled
  const skipAuth = (config as { skipAuth?: boolean }).skipAuth

  if (!skipAuth) {
    const token = tokenProvider.getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
  }

  // Add request ID for tracing/debugging
  config.headers["X-Request-ID"] = generateRequestId()

  // Add timestamp for debugging
  const requestTime = Date.now()
  config.headers["X-Request-Time"] = new Date(requestTime).toISOString()

  // Store start time for duration calculation
  ;(config as { _startTime?: number })._startTime = requestTime

  // Ensure Content-Type for non-GET requests without explicit Content-Type
  if (
    config.method &&
    !["get", "head", "options"].includes(config.method.toLowerCase()) &&
    !config.headers["Content-Type"] &&
    config.data
  ) {
    config.headers["Content-Type"] = "application/json"
  }

  // Log request
  logRequest(config)

  return config
}

/**
 * Request error interceptor
 */
export function requestErrorInterceptor(error: unknown): Promise<never> {
  // Log request errors in development
  if (import.meta.env.DEV) {
    console.error("[API Request Error]", error)
  }
  return Promise.reject(error)
}
